// SPDX-License-Identifier: GPL-3.0-or-later
// Score values, times and limits of the SigmaBots table (THEME.md sections 4, 6, 7, 8).
// Every score value is here and in SCORE_TABLE. No value is hidden or random.

import { MAX_SCORE_DELTA_PER_SEC, type ScoreRule } from "../../shared/contracts.ts";

/** Score values in points. */
export const POINTS = {
  outlane: 2_500,
  inlane: 1_000,
  orbitLeft: 10_000,
  orbitRight: 15_000,
  ramp: 20_000,
  tokenTarget: 2_500,
  tokenBank: 25_000,
  saucer: 5_000,
  spin: 100,
  spinLit: 500,
  bumper: 1_000,
  bumperBoost: 2_000,
  sling: 250,
  denyTarget: 5_000,
  denyBank: 25_000,
  meterFull: 15_000,
  combo: 30_000,
  merged: 50_000,
  skillShot: 50_000,
  skillOther: 5_000,
  superSkill: 100_000,
  // Accept All
  acceptAllFlatPerSecond: 5_000,
  acceptAllComplete: 40_000,
  // Bot Orbit Run
  worksOrbit: 15_000,
  worksComplete: 50_000,
  worksPerEarlierOrbit: 5_000,
  // Context Window Full
  contextTarget: 10_000,
  contextComplete: 75_000,
  // Bot Friday Deploy
  yoloComplete: 50_000,
  yoloGoal: 100_000,
  // SigmaBot Swarm
  jackpot: 50_000,
  superJackpot: 250_000,
  // ASK Mode: Prod Bot
  askDeny: 40_000,
  grandApproval: 500_000,
  // End-of-ball bonus per shot, before the bonus multiplier.
  bonusPerShot: 1_000,
} as const;

/** Times in milliseconds. */
export const TIMES = {
  skillLaneStepMs: 500,
  superSkillMs: 3_000,
  comboWindowMs: 4_000,
  spinnerLitMs: 10_000,
  tokenBurnPauseMs: 5_000,
  saucerHoldMs: 2_000,
  saucerModeSelectMs: 5_000,
  ballSaveLaunchMs: 10_000,
  ballSaveBankMs: 10_000,
  ballSaveSwarmMs: 10_000,
  denyResetMs: 1_000,
  accept: 30_000,
  works: 40_000,
  context: 45_000,
  yolo: 40_000,
  wizard: 90_000,
  /** The engine needs a little time to count the balls after addBall. */
  multiballGraceMs: 500,
  /** Randy countdown message: one message each step. */
  randyStepMs: 250,
} as const;

/** Counts and limits. */
export const LIMITS = {
  tokenBurnMeter: 25,
  bonusMultiplierMax: 5,
  tiltWarnings: 2,
  /** Ramp shots for the first lock, then for each next lock. */
  rampShotsFirstLock: 3,
  rampShotsNextLock: 2,
  /** Locked balls that start SigmaBot Swarm. */
  locksForMultiball: 2,
  /** Balls that SigmaBot Swarm adds when a lock starts it. */
  lockMultiballBalls: 2,
  /** Balls that a boss defeat adds (victory multiball). */
  victoryMultiballBalls: 2,
  /** Balls that clearing every target bank on one ball adds (2-ball multiball). */
  bankComboBalls: 1,
  /** The most balls in play at one time. Every multiball path stops here. */
  maxLiveBalls: 4,
  jackpotsForSuper: 4,
  askShotsPerGroup: 4,
  askDenyTotal: 8,
  worksOrbitsInBallForSpinner: 3,
  worksOrbitsToComplete: 3,
  acceptShotsToComplete: 3,
} as const;

/**
 * Score rate limit. The rules never give more than this many points through
 * `addScore` in any window of 1000 ms. A larger award waits in a queue and
 * pays out as the window allows (a score count-up). The queue is not lost at
 * the end of a ball (see BONUS_CAP).
 */
export const SCORE_RATE_CAP_PER_SECOND = 100_000;

/** The end-of-ball bonus, with the queue that is left, never gives more than this. */
export const BONUS_CAP = 140_000;

/**
 * The highest legal score gain in one second: the rate cap plus one bonus.
 * This value must stay below MAX_SCORE_DELTA_PER_SEC (the Durable Object clamp).
 */
export const MAX_SCORE_PER_SECOND = SCORE_RATE_CAP_PER_SECOND + BONUS_CAP;

if (MAX_SCORE_PER_SECOND >= MAX_SCORE_DELTA_PER_SEC) {
  throw new Error("pinball rules: MAX_SCORE_PER_SECOND must stay below MAX_SCORE_DELTA_PER_SEC");
}

/**
 * Every scoring rule of the table, as public text. THEME.md section 4 is the source.
 * A value that doubles in a mode has its own line.
 */
export const SCORE_TABLE: readonly ScoreRule[] = [
  { label: "Outlane rollover (Guard Lane, Force Push Lane)", points: POINTS.outlane },
  { label: "Inlane rollover (Rollback Lane, Revert Lane)", points: POINTS.inlane },
  { label: "Bot Orbit (left orbit)", points: POINTS.orbitLeft },
  { label: "10X Orbit (right orbit)", points: POINTS.orbitRight },
  { label: "Ghost Ramp or Merge Ramp", points: POINTS.ramp },
  { label: "Context Window drop target (TOKENS 1, 2 or 3)", points: POINTS.tokenTarget },
  { label: "Context Window bank (all 3 TOKENS targets)", points: POINTS.tokenBank },
  { label: "Prod Bot saucer shot", points: POINTS.saucer },
  { label: "Bot Spinner, each spin", points: POINTS.spin },
  { label: "Bot Spinner, each spin when lit", points: POINTS.spinLit },
  { label: "Token Burn pop bumper hit", points: POINTS.bumper },
  { label: "Token Burn pop bumper hit after the Context Window bank", points: POINTS.bumperBoost },
  { label: "Slingshot hit", points: POINTS.sling },
  { label: "Token Burn meter full (25 pop bumper hits)", points: POINTS.meterFull },
  { label: "DENY standup target (D, E, N or Y)", points: POINTS.denyTarget },
  { label: "DENY bank (the word D-E-N-Y)", points: POINTS.denyBank },
  { label: "Combo: two orbit or ramp shots in 4 s", points: POINTS.combo },
  {
    label: "Merged Ramps: ramp to ramp in 4 s (replaces the combo)",
    points: POINTS.merged,
  },
  { label: "Skill shot (lit top lane)", points: POINTS.skillShot },
  { label: "Top lane that is not lit", points: POINTS.skillOther },
  { label: "Super skill shot (Merge Ramp in 3 s)", points: POINTS.superSkill },
  { label: "Accept All: Bot Orbit, 2x", points: POINTS.orbitLeft * 2 },
  { label: "Accept All: 10X Orbit, 2x", points: POINTS.orbitRight * 2 },
  { label: "Accept All: ramp, 2x", points: POINTS.ramp * 2 },
  {
    label: "Accept All: DENY target, flat bonus for each second left",
    points: POINTS.acceptAllFlatPerSecond,
  },
  {
    label: "Accept All: completion (3 orbit or ramp shots, or a DENY target)",
    points: POINTS.acceptAllComplete,
  },
  { label: "Bot Orbit Run: each orbit", points: POINTS.worksOrbit },
  { label: "Bot Orbit Run: completion (3 orbits)", points: POINTS.worksComplete },
  {
    label: "Bot Orbit Run: bonus for each earlier left orbit in the game",
    points: POINTS.worksPerEarlierOrbit,
  },
  { label: "Context Window Full: each TOKENS target", points: POINTS.contextTarget },
  { label: "Context Window Full: completion", points: POINTS.contextComplete },
  { label: "Bot Friday Deploy: spinner spin, 2x", points: POINTS.spin * 2 },
  { label: "Bot Friday Deploy: spinner spin when lit, 2x", points: POINTS.spinLit * 2 },
  { label: "Bot Friday Deploy: ramp, 2x", points: POINTS.ramp * 2 },
  { label: "Bot Friday Deploy: completion (100,000 mode points)", points: POINTS.yoloComplete },
  { label: "SigmaBot Swarm: jackpot", points: POINTS.jackpot },
  { label: "SigmaBot Swarm: super jackpot (saucer)", points: POINTS.superJackpot },
  { label: "ASK Mode: each DENY shot", points: POINTS.askDeny },
  { label: "ASK Mode: Grand Approval (saucer)", points: POINTS.grandApproval },
  { label: "End of ball: each shot made, times the bonus multiplier", points: POINTS.bonusPerShot },
];
